/**
 * Capacitor bridge — runs once on app boot.
 *
 * Responsibilities (only when running INSIDE a Capacitor WebView):
 *   1. Status bar / safe-area: switch off WebView overlay so the OS draws the
 *      status bar above us (Flutter ``SafeArea`` behaviour). Color the bar so
 *      it matches the tenant theme.
 *   2. Push notifications: request permission, register with FCM/APNs, post
 *      the device token to ``/api/push/fcm/token`` so the backend can fan
 *      out order updates, and wire foreground/tap listeners.
 *
 * Safe to ``await initCapacitor()`` from a browser tab — it short-circuits
 * via ``Capacitor.isNativePlatform()``.
 */
import axios from "axios";
import { NOTIFICATION_SOUND_VERSION } from "./_generated_notification";
import { ROLE_LOCK } from "./buildLock";
import {
    CHANNEL_SUPPORT,
    NEW_ORDER_CHANNEL_ID,
    ORDERS_CHANNEL_ID,
    SILENT_CHANNEL_ID,
    androidChannelSpecs,
    bundledSoundFor,
    deviceBundledSoundName,
    legacyOrderChannelIds,
} from "./pushChannels";
import { PUSH_OPENED_EVENT, isPersistentPush } from "./orderAlert";
import { effectivePermissionState } from "./notificationPermission";

// Topics that mean "a NEW order needs action" (merchant: one just came
// in; rider: one's available to claim) as opposed to a routine status
// update on an order already in hand. Mirrors backend fcm.py's
// NEW_ORDER_TOPICS exactly — keep in sync if that set ever changes.
export const NEW_ORDER_TOPICS = new Set(["order_placed", "rider_order_available", "ride_available"]);

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Channel ids this device actually created, reported with the FCM token so the backend never targets a missing/silent one.
let _androidChannels = null;
// Whether the backend actually accepted this device's token. A granted OS permission is NOT the same as being
// registered, and treating it as such is how devices ended up silently unreachable — see ensurePushRegistration.
let _tokenRegistered = false;

/** Pick ``rider`` or ``customer`` to scope the FCM token registration so
 * backend dispatch (``notify.py``) can target the right app.
 *
 * Prefers the BUILD-TIME role lock over the current URL: the Rider APK
 * is unambiguously the rider app for its entire lifetime, and the
 * Customer APK is unambiguously "customer" (merchant/admin consoles
 * ship inside it but ride on the same push-token bucket). A path-only
 * check raced against login — ``AuthContext.login()`` calls
 * ``refreshFcmTokenAfterLogin()`` synchronously right after OTP verify,
 * while the browser is often still on the shared ``/login`` route and
 * hasn't yet navigated to ``/rider/...``. That first (and often only —
 * FCM tokens are long-lived) registration silently tagged every
 * rider's token ``app: "customer"`` instead of ``"rider"``, so
 * ``send_to_user(..., app="rider")`` never found a matching token —
 * reported as "rider push notifications not delivered". Only the
 * unlocked preview/dev build (every role's routes render from one
 * bundle) still needs the runtime path check. */
function detectApp() {
    if (ROLE_LOCK === "RIDER") return "rider";
    if (ROLE_LOCK === "CUSTOMER_OR_MERCHANT") return "customer";
    if (typeof window === "undefined") return "customer";
    return window.location.pathname.startsWith("/rider") ? "rider" : "customer";
}

/** Fetch THIS device's own tenant's new-order alert config at runtime.
 *
 * Why this can't be a build-time constant like ``NOTIFICATION_SOUND_*``
 * above: the platform-wide sound is genuinely one value for every
 * tenant, so baking it into the one shared frontend bundle (which is
 * what every tenant's Capacitor WebView loads — see
 * ``build_customer_app.py``'s "the APK is a WebView shell, the actual
 * JS is loaded from the live server" comment) is correct. A PER-TENANT
 * value cannot work the same way — there is only one deployed
 * ``_generated_notification.js`` live at a time, shared by every
 * tenant's app, so it can't hold different tenants' sounds
 * simultaneously. The tenant's own config has to be resolved at
 * runtime, the same way ``TenantContext`` resolves branding/theme.
 *
 * Uses the public, unauthenticated ``/api/tenant/by-slug`` endpoint —
 * already used by TenantContext for theme/branding, and
 * ``new_order_alert_config`` already passes through its redaction
 * (models.py:TENANT_SECRET_FIELDS) untouched, since it's non-secret
 * playback metadata (filename/extension/version/toggle), not a
 * credential. Returns ``null`` on any failure (offline first boot,
 * tenant not resolved yet, etc.) — callers must treat that exactly
 * like "tenant configured nothing", never as an error to surface. */
async function fetchTenantNewOrderSoundConfig() {
    if (!API) return null;
    try {
        const { resolveTenantSlug } = await import("./api");
        const slug = resolveTenantSlug();
        if (!slug) return null;
        const { data } = await axios.get(
            `${API}/api/tenant/by-slug/${encodeURIComponent(slug)}`,
            { timeout: 8000 },
        );
        // Rules come along too: they decide whether this tenant needs the distinct "New Orders" channel
        // (see pushChannels.wantsDistinctNewOrderChannel), and the backend gates on the same condition.
        return { alertCfg: data?.new_order_alert_config || null, rules: data?.notification_rules || null };
    } catch {
        return null;
    }
}

function getAuthHeader() {
    try {
        const tok = window.sessionStorage.getItem("bizil_token") ||
                    window.localStorage.getItem("bizil_token");
        return tok ? { Authorization: `Bearer ${tok}` } : {};
    } catch {
        return {};
    }
}

/** Configure the native status bar so content doesn't sit under it. The
 * tenant theme color is read from the live ``--primary`` CSS variable so
 * the bar tints to match Bizil green by default, or whatever the tenant
 * overrides it to via ``TenantContext``. */
async function setupStatusBar(Capacitor) {
    try {
        const { StatusBar, Style } = await import("@capacitor/status-bar");
        // Flutter-SafeArea equivalent: WebView is pushed BELOW the status bar.
        await StatusBar.setOverlaysWebView({ overlay: false });
        // Pick a status-bar background color from the live theme. Default to
        // a Bizil-green that contrasts well with the white-icon style.
        let bg = "#22C55E";
        try {
            const root = getComputedStyle(document.documentElement);
            const primary = root.getPropertyValue("--primary").trim();
            // ``--primary`` is in HSL space (``H S% L%``) — convert to a hex
            // string by routing through a temp DOM element so the browser
            // does the conversion for us.
            if (primary) {
                const probe = document.createElement("div");
                probe.style.color = `hsl(${primary})`;
                document.body.appendChild(probe);
                const rgb = getComputedStyle(probe).color;
                document.body.removeChild(probe);
                const m = rgb.match(/(\d+),\s*(\d+),\s*(\d+)/);
                if (m) bg = "#" + [1, 2, 3].map((i) => Number(m[i]).toString(16).padStart(2, "0")).join("");
            }
        } catch { /* fall back to default */ }
        await StatusBar.setBackgroundColor({ color: bg });
        await StatusBar.setStyle({ style: Style.Dark }); // light icons on green
    } catch (err) {
        console.warn("[capacitor] status-bar setup skipped:", err?.message || err);
    }
}

/** Register a single FCM token with the backend. ``platform`` is
 * ``android`` / ``ios`` so dispatch can target a specific OS later. */
async function uploadToken(token, platform) {
    if (!API || !token) return false;
    try {
        await axios.post(
            `${API}/api/push/fcm/token`,
            {
                token,
                app: detectApp(),
                platform,
                user_agent: navigator.userAgent,
                // Advertise the sound version baked into THIS APK at
                // build time. The backend stores it on the token doc
                // and uses it to construct the matching channel id
                // when dispatching FCM pushes — see ``backend/fcm.py``.
                sound_version: NOTIFICATION_SOUND_VERSION || 0,
                channels: platform === "android" ? _androidChannels : null,
                // Reported even when ``channels`` is null, so the backend can tell a bundle that failed to create
                // its channels from an older bundle whose legacy channel still exists. See fcm.send_fcm_v1.
                channel_support: platform === "android" ? CHANNEL_SUPPORT : null,
            },
            {
                headers: { ...getAuthHeader() },
                // The app's primary credential is the httpOnly auth cookie (iter61). Without this the request
                // carried ONLY the localStorage Bearer — which is minted once at OTP login and never renewed
                // (/auth/refresh re-mints cookies only), so a long-lived session 401s here on every launch and
                // the device is never registered. Measured in production before this line: 0 of 10 tenant
                // admins and 3 of 36 shop owners had a token, while customers — who re-login often and so
                // carry a fresh Bearer — were largely fine.
                withCredentials: true,
                timeout: 10000,
            },
        );
        _tokenRegistered = true;
        console.log("[capacitor] FCM token registered with backend.");
        return true;
    } catch (err) {
        _tokenRegistered = false;
        // No JWT yet? That's normal on cold start before login. The user
        // will register again after login because we re-call init then.
        const status = err?.response?.status;
        if (status === 401) {
            console.log("[capacitor] FCM token deferred — user not logged in yet.");
        } else {
            console.warn("[capacitor] FCM token upload failed:", status || err?.message);
        }
        return false;
    }
}

/**
 * Create this tenant's order channels (see pushChannels.js) and remove the old silent ones. Idempotent.
 *
 * Order matters, and it used to be wrong: the legacy channels were deleted unconditionally, OUTSIDE the try that
 * creates the new ones. A failure in there — ``fetchTenantNewOrderSoundConfig`` needs the network, so a cold start
 * on a bad connection is enough — left the device with the old channels deleted, the new ones never created, and
 * a token reporting none. Android drops a notification addressed to a channel that doesn't exist, and does it
 * silently: FCM still returns 200, the foreground listener still fires, so the app looks fine while every
 * background and closed-app notification vanishes. Nothing is deleted now until the replacements exist.
 */
async function ensureAndroidChannels(FirebaseMessaging) {
    let existing = [];
    try {
        existing = (await FirebaseMessaging.listChannels()).channels || [];
    } catch { /* listing unsupported (pre-Android 8) — nothing to migrate */ }
    let created = false;
    try {
        // A tenant config we couldn't fetch means "nothing distinct configured", never a reason to skip channels.
        let tenantCfg = null;
        try { tenantCfg = await fetchTenantNewOrderSoundConfig(); } catch { /* offline — use the defaults */ }
        // What THIS binary bundled, read off the WebView User-Agent the build stamped. An APK predating the
        // custom-sound feature has no marker, so it never creates a channel pointing at a res/raw file it
        // lacks — it keeps the default tone and keeps working, with no app update required.
        const deviceSound = deviceBundledSoundName(navigator?.userAgent);
        const specs = androidChannelSpecs(tenantCfg?.alertCfg || null, existing, tenantCfg?.rules || null, deviceSound);
        for (const spec of specs) await FirebaseMessaging.createChannel(spec);
        // Prefer the channel carrying the tenant's own sound when this build was confirmed to bundle it;
        // otherwise the soundless new-order channel, which rings the phone's default tone.
        const custom = bundledSoundFor(tenantCfg?.alertCfg || null, deviceSound);
        const customCreated = custom && specs.some((s) => s.id === custom.id);
        _androidChannels = {
            orders: ORDERS_CHANNEL_ID,
            new_order: customCreated ? custom.id
                : specs.some((s) => s.id === NEW_ORDER_CHANNEL_ID) ? NEW_ORDER_CHANNEL_ID : null,
            silent: specs.some((s) => s.id === SILENT_CHANNEL_ID) ? SILENT_CHANNEL_ID : null,
        };
        created = true;
        console.log("[capacitor] order channels ready:", _androidChannels);
    } catch (err) {
        console.warn("[capacitor] channel create failed:", err?.message || err);
    }
    // Only once the replacements are in place — otherwise the device is left with no usable order channel at all.
    if (!created) return;
    for (const id of legacyOrderChannelIds(existing.map((c) => c.id))) {
        try { await FirebaseMessaging.deleteChannel({ id }); } catch { /* already gone */ }
    }
}

async function setupPushNotifications(Capacitor) {
    try {
        // ``@capacitor-firebase/messaging`` is the canonical Capacitor 5
        // plugin for FCM-on-both-platforms. Under the hood it bundles
        // Firebase Messaging SDK on iOS (CocoaPods) and on Android uses
        // the same Firebase Messaging dep Capacitor's stock plugin
        // already pulls in. The key win over ``@capacitor/push-notifications``
        // (which we used previously): on iOS its ``getToken()`` returns
        // an **FCM registration token**, not a raw APNs hex token. Our
        // backend dispatches via FCM v1, which only accepts FCM tokens,
        // so this swap is what unblocks iOS push delivery.
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const platform = Capacitor.getPlatform();      // "ios" | "android"

        // Check (never auto-REQUEST here) — this function runs on EVERY
        // app boot via initCapacitor() below, unconditionally, before
        // the router even resolves whether the onboarding screen should
        // show. Android 13+ / iOS notification permission is one-shot:
        // once ANY answer is recorded (grant OR deny) the OS will never
        // show that dialog again. Calling requestPermissions() here
        // unconditionally meant this automatic boot-time call raced
        // ahead of and silently consumed the ONLY prompt the OS would
        // ever give, moments before the user reached the onboarding
        // screen's own explicit "Continue" tap (PermissionsPage.jsx) or
        // NotificationPermissionCard's "Enable notifications" button —
        // both call the exact same requestPushPermission() correctly,
        // but by then there was nothing left to ask, so the OS just
        // replayed the already-decided state with no visible dialog
        // (user-reported: tapping Continue didn't trigger the native
        // prompt). Skip the request entirely while state is still
        // undetermined ("prompt"/"prompt-with-rationale") and let one
        // of those two explicit, user-initiated flows be the ONLY place
        // that ever asks. Already-resolved states (granted from a past
        // request, or denied) fall through unchanged — re-checking
        // those is harmless and keeps token registration working
        // exactly as before for returning users.
        let perm = await FirebaseMessaging.checkPermissions();
        if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
            console.log("[capacitor] Push permission not yet decided — deferring to the onboarding screen / Profile card instead of auto-prompting.");
            return;
        }
        if (perm.receive !== "granted") {
            console.warn("[capacitor] Push permission denied by user.");
            return;
        }

        // Android plays the sound of the channel a push lands on (immutable once created) — see pushChannels.js.
        if (platform === "android") {
            await ensureAndroidChannels(FirebaseMessaging);
        }

        // iOS foreground delivery — by default iOS suppresses the alert
        // banner + sound when the app is in the foreground. Without this
        // call the user sees nothing when an order push arrives while
        // they're actively using the app (user-reported Feb 2026 silent-
        // push bug on iOS). With this enabled the FCM payload's
        // ``apns.aps.sound: "default"`` plays the standard alert tone
        // and the banner slides in from the top, matching Android's
        // heads-up behaviour.
        if (platform === "ios") {
            try {
                await FirebaseMessaging.setShouldShowNotificationsForForegroundApp?.({
                    shouldDeliver: true,
                });
                console.log("[capacitor] iOS foreground notifications enabled");
            } catch (err) {
                console.warn("[capacitor] iOS foreground delivery flag failed:", err?.message || err);
            }
        }


        // Listeners — declared BEFORE getToken() so we don't miss the
        // first delivery (FirebaseMessaging may emit on cold start with
        // a queued message from a notification tap).
        // Cleared first: this setup runs again when permission is granted mid-session (requestPushPermission).
        await FirebaseMessaging.removeAllListeners();
        FirebaseMessaging.addListener("tokenReceived", (event) => {
            // Fires both on initial registration AND on token rotation
            // (every ~6 months in normal use, or after app reinstall).
            console.log("[capacitor] FCM registration token:", event.token);
            window.__bizilFcmToken = event.token;
            uploadToken(event.token, platform);
        });
        FirebaseMessaging.addListener("notificationReceived", (event) => {
            // Foreground delivery — FirebaseMessaging on iOS doesn't show
            // the system banner when the app is in the foreground (unless
            // we set ``presentationOptions``), so we surface a toast ourselves.
            console.log("[capacitor] Push received (foreground):", event);
            const notif = event.notification || event;
            const topic = notif?.data?.topic;
            const isNewOrder = NEW_ORDER_TOPICS.has(topic);
            // Bug found in production (mani tenant): this used to gate
            // the louder/repeated chime + vibration purely on the
            // TOPIC being a new-order one — meaning EVERY tenant got
            // the urgent treatment whether they asked for it or not.
            // The backend now only sets ``data.loud_alert`` when the
            // RECEIVING tenant has explicitly enabled it for new-order
            // alerts (see fcm.py's per-tenant dispatch cascade) — key
            // off that instead, so a tenant who never opted in gets a
            // new-order push that's audibly identical to a routine one,
            // matching the native channel/sound split above (which is
            // ALSO gated per-tenant, not just per-topic).
            const loudAlert = notif?.data?.loud_alert === "1";
            // Persistent order alerts (OrderAlertHost) bring their own looping sound + vibration.
            // Off for this account → the normal chime + toast below, as before persistent alerts existed.
            const persistentAlert = isPersistentPush(notif);
            // Also play an in-app chime: on iOS the OS-level sound is
            // suppressed by default in foreground state, and on Android
            // it can be muted by the user's DND profile. This guarantees
            // at least an audible bell as long as the device isn't on
            // hardware silent / mute. Loud-mode alerts get the longer,
            // more insistent variant (and a vibration on Android, where
            // the WebView actually supports the Vibration API — iOS
            // WebKit never has) so they're distinct from a routine
            // status-update ding.
            if (!persistentAlert) (async () => {
                try {
                    const { playNotifSound } = await import("./notifSound");
                    playNotifSound(loudAlert ? { urgent: true } : undefined);
                } catch { /* notifSound is part of the bundle — should always import */ }
            })();
            if (loudAlert && !persistentAlert) {
                try {
                    navigator.vibrate?.([300, 150, 300, 150, 300]);
                } catch { /* not available (iOS WebKit) — sound alone still fires */ }
            }
            try {
                // ``isNewOrder`` still drives the toast styling/copy in
                // App.js (purely informational — "this is a new order",
                // not a sound/behavior change) regardless of the
                // tenant's loud-mode preference.
                const evt = new CustomEvent("bizil:push", {
                    detail: { ...notif, isNewOrder, loudAlert, persistentAlert },
                });
                window.dispatchEvent(evt);
            } catch { /* ignore */ }
        });
        FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
            // User tapped the notification (background or terminated state).
            console.log("[capacitor] Push tapped:", event);
            try {
                window.dispatchEvent(new CustomEvent(PUSH_OPENED_EVENT, { detail: event?.notification || {} }));
            } catch { /* ignore */ }
            const url = event?.notification?.data?.url;
            if (url) {
                try {
                    const u = new URL(url, window.location.origin);
                    if (u.origin === window.location.origin) {
                        window.history.pushState({}, "", u.pathname + u.search);
                        window.dispatchEvent(new PopStateEvent("popstate"));
                    } else {
                        window.location.href = url;
                    }
                } catch {
                    window.location.href = url;
                }
            }
        });

        // Kick off registration. ``getToken()`` returns the current FCM
        // token (waits internally for APNs registration on iOS, then
        // exchanges the APNs token for an FCM token via Firebase).
        const { token } = await FirebaseMessaging.getToken();
        if (token) {
            console.log("[capacitor] FCM token (initial):", token);
            window.__bizilFcmToken = token;
            uploadToken(token, platform);
        }
    } catch (err) {
        console.warn("[capacitor] push setup failed:", err?.message || err);
    }
}

let _initStarted = false;

export async function initCapacitor() {
    if (_initStarted) return;
    _initStarted = true;
    if (typeof window === "undefined") return;
    let Capacitor;
    try {
        ({ Capacitor } = await import("@capacitor/core"));
    } catch {
        return;
    }
    if (!Capacitor?.isNativePlatform?.()) {
        return; // Plain browser — nothing to do.
    }
    // Tag <html> so the existing CSS rules (`html.capacitor-platform …`)
    // know we're inside a native shell and can branch accordingly.
    document.documentElement.classList.add("capacitor-platform");
    document.documentElement.classList.add(`capacitor-${Capacitor.getPlatform()}`);

    await setupStatusBar(Capacitor);
    await setupPushNotifications(Capacitor);
}

/** Run ``callback`` every time the native app comes back to the
 * foreground (Android/iOS "resume" — user switched back from another
 * app, unlocked the screen, etc). Used by ``AuthContext`` to re-check
 * auth state on resume: if a request was in flight when the OS
 * suspended the WebView's network stack (backgrounding / Doze mode),
 * it can be left permanently hanging, and the resuming app looks
 * "stuck on loading" until force-closed. Re-firing the check on
 * resume gives it a fresh chance to complete even if the original
 * hung request never settles.
 *
 * No-op on web (guarded by ``isNativePlatform()``, same as the rest of
 * this module). Returns an unsubscribe function; safe to call the
 * returned function multiple times or not at all (component unmount
 * cleanup is best-effort here, same as elsewhere in this file).
 */
export async function onAppResume(callback) {
    if (typeof window === "undefined") return () => {};
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor?.isNativePlatform?.()) return () => {};
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
            if (isActive) callback();
        });
        return () => { try { handle.remove(); } catch { /* ignore */ } };
    } catch {
        return () => {};
    }
}

/** Run ``callback(isActive)`` whenever the native app moves between foreground and background. Same
 * contract as ``onAppResume``: no-op on web, resolves to an unsubscribe function. */
export async function onAppStateChange(callback) {
    if (typeof window === "undefined") return () => {};
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor?.isNativePlatform?.()) return () => {};
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => callback(!!isActive));
        return () => { try { handle.remove(); } catch { /* ignore */ } };
    } catch {
        return () => {};
    }
}

/** Android's own answer to "will this app's notifications be shown?" — ``{enabled, sdk}``, or null where it isn't
 * available (iOS, or an APK built before AppSettings.areNotificationsEnabled existed: the call rejects as not
 * implemented, and those devices keep exactly the behaviour they had). Read-only; see notificationPermission.js
 * for why the plugin's own "granted" can't be trusted below Android 13. */
async function readOsNotificationState(Capacitor) {
    if (Capacitor?.getPlatform?.() !== "android") return null;
    try {
        const { registerPlugin } = await import("@capacitor/core");
        const r = await registerPlugin("AppSettings").areNotificationsEnabled();
        return { enabled: r?.enabled, sdk: r?.sdk };
    } catch {
        return null;
    }
}

/**
 * Make sure this device is registered for push, and return the permission state.
 *
 * Boot deliberately skips the whole of ``setupPushNotifications`` while permission is still undecided, so it
 * can't consume the one prompt the OS will ever show (see the comment there). Until this existed, the ONLY way
 * back from that state was the customer onboarding screen or the customer/rider profile card — and neither is
 * reachable from the merchant or admin console, which render outside the customer shell. A shop owner who lived
 * in ``/merchant`` therefore never got asked, never got a token, and never received a single push; what still
 * worked in the foreground was the Live Orders refresh, not a notification. ``NotificationGate`` calls this from
 * those consoles, and login calls it too so a device that granted permission before signing in registers at once.
 *
 * Never prompts by itself — it only picks up work boot had to defer. Safe to call repeatedly.
 */
export async function ensurePushRegistration() {
    if (typeof window === "undefined") return { state: "web" };
    let Capacitor;
    try {
        ({ Capacitor } = await import("@capacitor/core"));
    } catch {
        return { state: "web" };
    }
    if (!Capacitor?.isNativePlatform?.()) return { state: "web" };
    let state = "unknown";
    try {
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        state = (await FirebaseMessaging.checkPermissions())?.receive || "unknown";
    } catch {
        return { state: "error" };
    }
    if (state !== "granted") return { state, registered: false };
    if (window.__bizilFcmToken) {
        // Already registered this session — re-upload so the token lands against the user who just signed in.
        await uploadToken(window.__bizilFcmToken, Capacitor.getPlatform());
    } else {
        // Granted, but boot skipped setup (permission was undecided then, or the plugin wasn't ready).
        await setupPushNotifications(Capacitor);
    }
    // ``registered`` is the part that actually matters. The OS permission being granted says nothing about
    // whether the backend has this device — and when the two disagree the app looks fine while receiving
    // nothing at all, which is exactly how this went unnoticed.
    // Registration above still follows the plugin's own answer, exactly as before. Only the state the screens
    // are told is corrected: below Android 13 the plugin says "granted" even when Android hides every notification.
    const shown = effectivePermissionState(state, await readOsNotificationState(Capacitor));
    return { state: shown, token: window.__bizilFcmToken || null, registered: _tokenRegistered };
}

/** Re-upload the FCM token after the user logs in so the backend has a
 * (user_id, token) row to dispatch from. Safe to call multiple times. */
export function refreshFcmTokenAfterLogin() {
    // Fire-and-forget: login must not wait on the push stack. When a token is already in memory this re-uploads
    // it against the new user; when permission was granted but boot deferred setup, this finally registers it.
    ensurePushRegistration().catch(() => {});
}

// iter134t — public helpers so the profile UI can render a
// "Notifications" panel that shows current permission status +
// lets the user re-request permission manually. Users who tap
// "Don't allow" on first install can never see the system dialog
// again (Android 13+ / iOS both suppress re-prompts). This retry
// button opens the OS Settings page for the app so they can flip
// the switch there — the ONLY way to recover after a denial.
export async function getPushPermissionStatus() {
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor?.isNativePlatform?.()) {
            return { supported: false, state: "web" };
        }
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const p = await FirebaseMessaging.checkPermissions();
        return {
            supported: true,
            // "granted" | "denied" | "prompt" | "unknown" — "denied" also when Android < 13 has them switched off.
            state: effectivePermissionState(p?.receive || "unknown", await readOsNotificationState(Capacitor)),
            token: window.__bizilFcmToken || null,
        };
    } catch (e) {
        return { supported: false, state: "error", error: e?.message || String(e) };
    }
}

/** Ask the OS for push permission. Returns the new state. On Android
 * 13+ / iOS this is a one-shot: if the user previously tapped "Don't
 * allow" the OS won't show the dialog — the returned state stays
 * "denied" and the caller should route the user to system settings
 * (see ``openAppNotificationSettings`` below). */
export async function requestPushPermission() {
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor?.isNativePlatform?.()) return { state: "web" };
        const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
        const p = await FirebaseMessaging.requestPermissions();
        const state = p?.receive || "unknown";
        if (state === "granted") {
            // Boot-time setup skipped everything while permission was undecided — create the
            // channels and listeners now, then register the token, instead of waiting for the next launch.
            await setupPushNotifications(Capacitor);
        }
        // Below Android 13 "granted" is the plugin's default, not an answer — report "denied" when Android has this
        // app's notifications off, so the caller sends the user to Settings instead of claiming success.
        return { state: effectivePermissionState(state, await readOsNotificationState(Capacitor)) };
    } catch (e) {
        return { state: "error", error: e?.message || String(e) };
    }
}

/** Deep-link to the OS notification settings page for THIS app so a
 * user who previously denied can flip the switch back on.
 *
 * Previously tried @capacitor/app-launcher's openUrl() with a
 * ``"app-settings:notification"`` string, falling back to
 * ``"package:<pkg>"`` — but AppLauncher.openUrl() can only fire a
 * generic ``android.intent.action.VIEW`` intent, and Android's
 * Settings app doesn't register a handler for ACTION_VIEW against
 * either of those values. Confirmed on a real connected device:
 * ``adb shell am start -a android.intent.action.VIEW -d "app-settings:
 * notification"`` -> "unable to resolve Intent", vs. the same call
 * with the correct action (``android.settings.APP_NOTIFICATION_
 * SETTINGS`` + the package as an extra) opening the real screen. So
 * this never actually worked on Android despite looking "proven" —
 * nobody had verified it on-device. Now uses the same custom
 * ``AppSettingsPlugin`` native plugin ``geolocation.js:openAppSettings()``
 * uses for the app-info screen (see that file's docstring for the
 * full writeup) — REQUIRES A NATIVE REBUILD, this is genuine native
 * Java code. */
export async function openAppNotificationSettings() {
    try {
        const { Capacitor } = await import("@capacitor/core");
        if (!Capacitor?.isNativePlatform?.()) return false;
        const platform = Capacitor.getPlatform();
        if (platform === "ios") {
            // iOS has no "wrong intent action" problem — app-settings:
            // is a genuine URL scheme the OS resolves directly.
            const { AppLauncher } = await import("@capacitor/app-launcher");
            const r = await AppLauncher.openUrl({ url: "app-settings:" });
            return !!(r && r.completed);
        }
        // Wrapped in ``{ plugin }`` rather than awaited/returned bare —
        // see the comment on ``_batteryPlugin()`` below for why.
        const { registerPlugin } = await import("@capacitor/core");
        const plugin = registerPlugin("AppSettings");
        const r = await plugin.openNotificationSettings();
        return !!(r && r.opened);
    } catch {
        return false;
    }
}

/** Bridge to the custom native ``BatteryOptimizationPlugin`` (Android
 * only — see scripts/_build_common.py:patch_battery_optimization_plugin
 * for the full root-cause writeup and the plugin's own Java source).
 * ``registerPlugin`` is Capacitor's standard way to bind to a plugin
 * that ships baked into the native project rather than as an npm
 * package — it resolves at call time, so this is safe to import even
 * on platforms/builds where the native class doesn't exist (the call
 * just rejects, caught below).
 *
 * Returns ``{ plugin }`` — NEVER the bare plugin object — because
 * Capacitor's native-bridge proxy answers "yes, callable" to literally
 * any property probe (it has no fixed method list on the JS side), so
 * awaiting the raw proxy directly (or returning it from an ``async``
 * function, which implicitly runs it through ``Promise.resolve()``)
 * makes the JS engine's thenable duck-typing find a ``.then`` and
 * invoke it AS a plugin method call — surfacing as the very confusing
 * runtime error "BatteryOptimization.then() is not implemented on
 * android" (user-reported). Wrapping in a plain object sidesteps the
 * duck-typing entirely; only ``plugin.someRealMethod()``'s OWN returned
 * promise should ever be awaited. */
async function _batteryPlugin() {
    const { registerPlugin, Capacitor } = await import("@capacitor/core");
    if (!Capacitor?.isNativePlatform?.() || Capacitor.getPlatform() !== "android") return { plugin: null };
    return { plugin: registerPlugin("BatteryOptimization") };
}

/** Whether this app is already exempted from Android's battery
 * optimization. Always ``true`` on web/iOS (nothing to exempt). */
export async function isIgnoringBatteryOptimizations() {
    try {
        const { plugin } = await _batteryPlugin();
        if (!plugin) return true;
        const r = await plugin.isIgnoringBatteryOptimizations();
        return !!r?.ignoring;
    } catch {
        return true;
    }
}

/** Fire the OS "Allow this app to ignore battery optimizations?" system
 * dialog directly (``Settings.ACTION_REQUEST_IGNORE_BATTERY_OPTIMIZATIONS``).
 * No-op (resolves ``false``) on web/iOS. Some OEMs (esp. MIUI) block
 * the direct dialog outright — the native side falls back to the
 * app-details settings screen automatically when that happens. */
export async function requestBatteryOptimizationExemption() {
    try {
        const { plugin } = await _batteryPlugin();
        if (!plugin) return false;
        const r = await plugin.requestExemption();
        return !!r?.requested;
    } catch {
        return false;
    }
}

/** Open the OEM-specific "Autostart" / background-launch settings
 * screen when the device manufacturer is recognised (Xiaomi, Oppo,
 * Vivo, OnePlus, Honor, Huawei), else falls back to the generic
 * app-details settings screen (Battery is one tap from there). No-op
 * on web/iOS. */
export async function openAutostartSettings() {
    try {
        const { plugin } = await _batteryPlugin();
        if (!plugin) return false;
        const r = await plugin.openAutostartSettings();
        return !!(r?.opened);
    } catch {
        return false;
    }
}
